import { logDebug, logError } from '@ptek/client-logger';
import { useCallback, useEffect, useRef } from 'react';
import { GeneratedDocument } from '../types/document';

/**
 * Document generation timeout in milliseconds (120 seconds)
 * Document generation can take time due to multiple Bedrock API calls.
 *
 * This timeout is ALWAYS-ON and flag-independent (NEB-96499): it owns the only
 * client-side bound on document generation and aborts the in-flight request via
 * an AbortController on expiry, preventing the permanent "generating" spinner.
 */
const DOCUMENT_GENERATION_TIMEOUT_MS = 120000;

export const TIMEOUT_ERROR_MESSAGE = 'Document generation timeout';

type GenerateDocumentOptions = {
  templateId?: string;
  transcript?: string;
  clearExistingDocuments?: boolean;
  signal?: AbortSignal;
};

/**
 * Hook for document generation with always-on timeout + abort handling.
 *
 * A 120-second timeout protects every caller regardless of
 * FEATURE_FLAGS.CONNECTION_RETRY. On expiry the wrapper aborts the in-flight
 * request (via an AbortController plumbed down to fetch) and rejects with
 * {@link TIMEOUT_ERROR_MESSAGE} so the UI surfaces a visible error instead of
 * hanging.
 *
 * @param generateDocument - The original generateDocument function to wrap
 * @returns generateDocument function with timeout + abort handling
 */
export function useDocumentGeneration(
  generateDocument: (
    options?: GenerateDocumentOptions
  ) => Promise<GeneratedDocument | null>
): (options?: GenerateDocumentOptions) => Promise<GeneratedDocument | null> {
  const timeoutRef = useRef<number | null>(null);

  /**
   * Clears any active timeout timer
   */
  const clearTimeoutTimer = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  /**
   * generateDocument wrapped with an always-on timeout + abort.
   */
  const generateDocumentWithTimeout = useCallback(
    async (
      options?: GenerateDocumentOptions
    ): Promise<GeneratedDocument | null> => {
      clearTimeoutTimer();

      const startTime = Date.now();
      const transcriptLength = options?.transcript?.length || 0;

      logDebug('useDocumentGeneration', 'Starting document generation', {
        templateId: options?.templateId,
        transcriptLength,
        timeoutMs: DOCUMENT_GENERATION_TIMEOUT_MS,
      });

      // AbortController owned by the timeout: on expiry we abort the in-flight
      // request so a hung fetch actually unwinds rather than being abandoned.
      const abortController = new AbortController();

      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutRef.current = window.setTimeout(() => {
          abortController.abort();
          reject(new Error(TIMEOUT_ERROR_MESSAGE));
        }, DOCUMENT_GENERATION_TIMEOUT_MS);
      });

      try {
        const result = await Promise.race([
          generateDocument({ ...options, signal: abortController.signal }),
          timeoutPromise,
        ]);

        clearTimeoutTimer();

        const duration = Date.now() - startTime;

        logDebug('useDocumentGeneration', 'Document generation completed', {
          templateId: options?.templateId,
          duration,
          hasResult: !!result,
        });

        return result;
      } catch (error) {
        clearTimeoutTimer();

        const duration = Date.now() - startTime;
        const isTimeout =
          error instanceof Error && error.message === TIMEOUT_ERROR_MESSAGE;

        logError('useDocumentGeneration', 'Document generation failed', {
          isTimeout,
          templateId: options?.templateId,
          transcriptLength,
          duration,
          message: error instanceof Error ? error.message : 'Unknown error',
        });

        throw error;
      }
    },
    [generateDocument, clearTimeoutTimer]
  );

  /**
   * Cleanup timeout on unmount
   */
  useEffect(() => {
    return () => {
      clearTimeoutTimer();
    };
  }, [clearTimeoutTimer]);

  return generateDocumentWithTimeout;
}
